import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Helmet } from 'react-helmet';
import { useSubscription } from '@apollo/client';
import { Alert, Button } from 'antd';
import { useGlobalSubscriptions } from 'meeopp-redux/global/subscriptions';
import { NavigateModal } from 'components/NavigateModal';

import { Loader } from 'components';
import { DEPLOY_NOTIFICATION_RECEIVED } from '../graphql/modules/shared';
import {
  CustomHeaderComponentProvider,
  CustomHeaderPropsProvider,
} from 'lib/header/useCustomHeader';

import { GlobalInitializer } from './Initialization/GlobalInitializer';
import { FeatureFlagProvider } from '../contexts/feature-flag-provider';

const App = React.memo((props) => {
  const { children, isLoading, user } = props;

  useGlobalSubscriptions();

  const { data: deployNotificationData } = useSubscription(DEPLOY_NOTIFICATION_RECEIVED, {
    variables: {
      hostname: window.location.hostname,
    },
  });

  const htmlLang = user?.preferredLocale ?? 'en';

  return (
    <FeatureFlagProvider user={user}>
      <GlobalInitializer user={user} ready={!isLoading} />
      <CustomHeaderComponentProvider>
        <CustomHeaderPropsProvider>
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
            <html lang={htmlLang} />
          </Helmet>
          <Loader spinning={isLoading} fullScreen={true} />
          {deployNotificationData?.deployNotificationReceived && (
            <Alert
              className="app-alert-update"
              type="warning"
              message="New updates are available!"
              action={
                <Button type="link" onClick={() => window.location.reload(true)}>
                  Reload
                </Button>
              }
            />
          )}
          {!isLoading && children}
          <NavigateModal />
        </CustomHeaderPropsProvider>
      </CustomHeaderComponentProvider>
    </FeatureFlagProvider>
  );
});

App.displayName = 'App';

App.propTypes = {
  children: PropTypes.element.isRequired,
  location: PropTypes.object,
  user: PropTypes.object,
  isLoading: PropTypes.bool,
};

const mapState = ({ loading, auth }) => ({
  isLoading: !!loading['app'],
  user: auth.user,
});

export default withRouter(connect(mapState)(App));
